import {
  ISuppliersResourceCategory,
  ISuppliersResult,
  getDoctorSupplierNames,
  getSupplierResources,
} from 'API/Suppliers/suppliers';
import { IDoctorsSuppliers } from 'Interfaces/doctorsSuppliers';
import { ITherapySuppliers } from 'Interfaces/therapySuppliers';
import {
  createContext,
  useCallback,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { IAction, SuppliersReducer } from 'reducer/Suppliers/SuppliersReducer';
import { ICoordinate } from '..';
import { SUPPLIERS_ID } from 'Enum/suppliers';

export interface ISuppliersDoctors {
  results: ISuppliersResult[];
  filteredResults: ISuppliersResult[];
}

export interface IDoctorsFilters extends IDoctorsSuppliers {
  orderByAscendingAlphabet?: boolean;
  orderByDescendingAlphabet?: boolean;
  location: ICoordinate | null;
}

export interface IDoctorsSuppliersContext {
  suppliers: ISuppliersDoctors;
  dispatchSuppliers: React.Dispatch<IAction>;
  resources: ISuppliersResourceCategory;
  supplierNames: string[];
  filters: Partial<IDoctorsFilters>;
  setFilters: (filters: Partial<IDoctorsFilters>) => void;
  resetFiltersOnSpecialtyChange: (specialty: number) => void;
  isLoading: boolean;
}

export const DoctorsSuppliersContext = createContext<IDoctorsSuppliersContext>({
  resources: {},
  supplierNames: [],
  filters: {},
  setFilters: () => {},
  resetFiltersOnSpecialtyChange: () => {},
  suppliers: {
    results: [],
    filteredResults: [],
  },
  dispatchSuppliers: () => {},
  isLoading: false,
});

export const defaultDoctorsFilters = {
  supName: '',
  specialty: null,
  area: null,
  region: null,
  city: [],
  accessible: null,
  gender: null,
  homeVisit: null,
  lng: [],
  location: null,
};

const doctorsSpecialtyId = 5;

export const DoctorsSuppliersProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [suppliers, dispatchSuppliers] = useReducer(SuppliersReducer, {
    results: [],
    filteredResults: [],
  });
  const [resources, setResources] = useState<ISuppliersResourceCategory>({});
  const [supplierNames, setSupplierNames] = useState<string[]>([]);
  const [filters, setFilters] = useState<Partial<ITherapySuppliers>>({
    ...defaultDoctorsFilters,
  });
  const [isLoading, setIsLoading] = useState(true);

  const initResources = useCallback(async () => {
    const resources = await getSupplierResources(SUPPLIERS_ID.doctors);
    if (resources) {
      resources.OCCUP = { ...resources.SPECL };
      delete resources.SPECL;
      setResources(resources);
    }
  }, [setResources]);

  const initSupplierNames = useCallback(async () => {
    const newNames = await getDoctorSupplierNames(filters.specialty!);
    if (newNames) setSupplierNames(newNames);
  }, [filters.specialty]);

  const init = useCallback(async () => {
    await initResources();
    if (filters.specialty) await initSupplierNames();
    else setSupplierNames([]);
    setIsLoading(false);
  }, [filters.specialty, initSupplierNames]);

  useEffect(() => {
    init();
  }, [init]);

  const resetFiltersOnSpecialtyChange = (specialty: number) => {
    setFilters({
      supName: '',
      specialty: specialty,
      area: null,
      region: null,
      city: [],
      accessible: null,
      gender: null,
      homeVisit: null,
      lng: [],
    });
  };

  return (
    <DoctorsSuppliersContext.Provider
      value={{
        suppliers,
        dispatchSuppliers,
        resources,
        supplierNames,
        filters,
        setFilters,
        resetFiltersOnSpecialtyChange,
        isLoading,
      }}
    >
      {children}
    </DoctorsSuppliersContext.Provider>
  );
};
